import React from "react";
import {useTranslation} from "react-i18next";

import useDialog from "../../../components/useDialog";

import MDButton from "materialDashboard/components/MDButton";
import MDBox from "materialDashboard/components/MDBox";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import FormControl from "@mui/material/FormControl";
import RadioGroup from "@mui/material/RadioGroup";
import {useCreateModel23ExportTask, useModels23ExportMutation} from "../query";
import MDLoadingButton from "../../../components/MDLoadingButton";
import WorkgroupAutocomplete from "../../../components/inputs/WorkgroupsAutocomplete/WorkgroupAutocomplete";
import {Alert} from "@mui/material";
import ExportTaskDialogContent from "../../../components/ExportTaskDialogContent";


const useExportState = ({close, filters}) => {
    const [format, setFormat] = React.useState("CSV");

    const model23SyncExportMutation = useModels23ExportMutation();
    const model23AsyncExportMutation = useCreateModel23ExportTask({async: true});

    const [exportTaskId, setExportTaskId] = React.useState(null);

    const [workgroupId, setWorkgroupId] = React.useState(null);

    // State to manage asynchronous export option, in case of async export then the app should go
    // in polling mode to check the status of the export
    const [asyncExport, setAsyncExport] = React.useState(false);

    const handleClose = () => {
        close();
        setExportTaskId(null);
    }

    const handleExport = async () => {
        if (asyncExport) {
            const data = await model23AsyncExportMutation.mutateAsync({
                workgroupId: workgroupId,
                filters: filters,
                data: {format}
            });
            setExportTaskId(data.id);
        } else{
            await model23SyncExportMutation.mutateAsync(filters);
            close();
        }
    }

    const handleChangeFormat = (event) => {
        setFormat(event.target.value);
    };

    const handleChangeAsyncExport = (event) => {
        setAsyncExport(event.target.value === 'true');
    }

    const handleChangeWorkgroupId = (newValue) => {
        setWorkgroupId(newValue);
    }

    return {
        format,
        handleChangeFormat,
        handleExport,
        asyncExport,
        handleChangeAsyncExport,
        model23SyncExportMutation,
        isLoading: model23SyncExportMutation.isLoading || model23AsyncExportMutation.isLoading,
        handleChangeWorkgroupId,
        workgroupId,
        exportTaskId,
        handleClose
    }
}


const ExportDialog = ({isOpen, close, filters={}}) => {

    const exportState = useExportState({close, filters});

    return <Dialog onClose={exportState.handleClose} open={isOpen} maxWidth="lg" fullWidth>
        {
            exportState.exportTaskId === null ?
                <ExportModel1DialogContent exportState={exportState}/>
                :
                <ExportTaskDialogContent exportTaskId={exportState.exportTaskId} handleClose={exportState.handleClose}/>
        }
    </Dialog>
}


const ExportModel1DialogContent = ({exportState}) => {
    const {t} = useTranslation(["models23", "validation"]);
    return <>
        <DialogTitle>{t('export.title')}</DialogTitle>
        <DialogContent>
            <FormControl fullWidth>
                {t('export.exportMode')}
                <RadioGroup
                    name="asyncExport"
                    value={exportState.asyncExport}
                    onChange={exportState.handleChangeAsyncExport}>
                    <Grid container>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Radio/>}
                                label={t('export.syncExportMode')}
                                value={false}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Radio/>}
                                label={t('export.asyncExportMode')}
                                value={true}
                            />
                        </Grid>
                    </Grid>
                </RadioGroup>
            </FormControl>
            {
                exportState.asyncExport && <WorkgroupAutocomplete value={exportState.workgroupId}
                                                                  onChange={exportState.handleChangeWorkgroupId}
                                                                  MDInputProps={{
                                                                      label: t('export.workgroupLabel'),
                                                                      error: !Boolean(exportState.workgroupId),
                                                                      helperText: Boolean(exportState.workgroupId) ? "" : t('validation:required')
                                                                  }}
                                                                  sx={{mt: 3}}/>
            }
            <Alert severity="info" sx={{mt: 3}}>
                {
                    exportState.asyncExport && t('export.asyncExportModeDescription')
                }
                {
                    !exportState.asyncExport && t('export.syncExportModeDescription')
                }
            </Alert>
        </DialogContent>
        <DialogActions>
            <MDButton onClick={exportState.handleClose} color="info" variant="outlined">
                {t('export.cancel')}
            </MDButton>
            <MDLoadingButton onClick={exportState.handleExport}
                             color="info"
                             variant="gradient"
                             disabled={exportState.asyncExport && !exportState.workgroupId}
                             loading={exportState.isLoading}>
                {t('export.buttonLabel')}
            </MDLoadingButton>
        </DialogActions>
    </>
}


const ExportToolbar = ({models23Filters}) => {
    const {t} = useTranslation(["models23"]);
    const dialogState = useDialog();

    return <MDBox py={2} px={3}>
        <MDLoadingButton variant="outlined"
                         color="info"
                         onClick={dialogState.open}
                         sx={{alignSelf: "flex-start"}}>
            {t('export.buttonCsvLabel')}
        </MDLoadingButton>
        <ExportDialog isOpen={dialogState.isOpen}
                      close={dialogState.close}
                      filters={models23Filters}/>
    </MDBox>
}

export default ExportToolbar;